import { FC, useEffect } from 'react';
import { TrialRunnerContextProvider, useExperimentContext } from 'context';
import { useViolationTracking } from 'utility/useViolationTracking'; // <-- 1. Import your hook
import {
    IntroductionHandler,
    ConsentHandler,
    CalibrationHandler,
    InformHandler,
    TrialRunnerHandler,
    ConclusionHandler
} from './stages';

/**
 * Root of the experiment. Initializes data that will never be set again.
 */
export const ExperimentRunner: FC = () => {
    const { expConfig, content, headerToIndexMap, stimulusData, srcToAudioMap } = useExperimentContext();

    const isAvailable = () => {
        if (!(expConfig && expConfig.availability)) return true;

        const now = new Date();
        const startDate = new Date(expConfig.availability.startDate);
        const endDate = new Date(expConfig.availability.endDate);
        if (now > startDate && now < endDate) {
            return true;
        }
        return false;
    };

    if (expConfig && stimulusData && content && headerToIndexMap && srcToAudioMap) {
        return <>{isAvailable() ? <StageHandler /> : <h3>Experiment not available!</h3>}</>;
    }
    return <></>;
};

/**
 * Handles what the current stage type of the experiment is.
 */
const StageHandler: FC = () => {
    // 2. Extract your context variables (You will need a setter for the logs)
    const { 
        currentStage, 
        experimentResults, 
        activeTrialIdentifier, 
        setExperimentViolationLogs,
        pcodeurl 
    } = useExperimentContext(); 

    if (!currentStage) throw new Error('No stage found');

    // extract pcode
    const currentPcode = pcodeurl || "unknown_pcode";

    // trial number
    const trackedTrial = activeTrialIdentifier || "unknown_trial";

    // 3. Initialize the hook. It will ONLY track if the user is in the 'trialRunner' stage.
    const isTrackingActive = currentStage.type === 'trialRunner';
    const { getLogs } = useViolationTracking(isTrackingActive, currentPcode, trackedTrial);

    // 4. Save the logs to context when leaving the trialRunner stage
    useEffect(() => {
        // If the stage changes away from trialRunner (e.g., to 'conclusion'), 
        // grab the logs and store them globally so your SaveData class can access them.
        if (currentStage.type === 'conclusion') {
            const finalLogs = getLogs();
            
            // NOTE: You will need to add this setter function to your useExperimentContext
            if (setExperimentViolationLogs) {
                setExperimentViolationLogs(finalLogs);
            }
        }
    }, [currentStage.type, getLogs, setExperimentViolationLogs]);

    // Determines what stage type this stage is.
    switch (currentStage.type) {
        case 'introduction': {
            return <IntroductionHandler />;
        }
        case 'consent': {
            return <ConsentHandler />;
        }
        case 'calibration': {
            return <CalibrationHandler />;
        }
        case 'inform': {
            return <InformHandler />;
        }
        case 'trialRunner': {
            return (
                <TrialRunnerContextProvider>
                    <TrialRunnerHandler />
                </TrialRunnerContextProvider>
            );
        }
        case 'conclusion': {
            return <ConclusionHandler />;
        }
        default: {
            throw new Error(`Unhandled stage type`);
        }
    }
};